import Badge from '../badge/Badge';
import {
    components,
    ControlProps,
    DropdownIndicatorProps,
    InputProps,
    MenuProps,
    MenuListProps,
    OptionProps,
    SingleValueProps,
    ClearIndicatorProps,
    MultiValueProps,
    PlaceholderProps,
    IndicatorsContainerProps,
} from 'react-select';

import { CheckIcon, ChevronDownIcon, ChevronUpDownIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { useContext } from 'react';
import { SelectContext } from './SelectContext';
import { SelectOption } from './types';
import { TooltipV2 } from '../tooltip/TooltipV2';

export function Control({ children, ...props }: ControlProps): JSX.Element {
    const { size, borderStyle, errorMessage } = useContext(SelectContext);
    const disabledStyle = props.isDisabled ? 'opacity-50' : '';
    const focusedStyle = props.isFocused
        ? borderStyle === 'border'
            ? `outline-none ring-1 ${errorMessage ? 'ring-0 border-red-600' : 'ring-indigo-500 border-indigo-500'}`
            : `outline-none ring-0 ${errorMessage ? 'border-red-600' : 'border-indigo-500'}`
        : `${errorMessage ? 'border-red-600' : ' border-gray-300'}`;
    const sizeStyle = size === 'standard' ? '' : 'min-h-[2rem]';
    const _borderStyle = borderStyle === 'border' ? 'border rounded-md shadow-sm' : 'border-0 border-b rounded-none';

    return (
        <components.Control
            {...props}
            className={`items-center bg-white dark:bg-white/[0.12] dark:border-0 text-left sm:text-sm ${focusedStyle} ${_borderStyle} ${sizeStyle} ${disabledStyle}`}
        >
            {children}
        </components.Control>
    );
}

export function DropdownIndicator({ children, ...props }: DropdownIndicatorProps): JSX.Element {
    const { borderStyle } = useContext(SelectContext);
    return (
        <components.DropdownIndicator {...props} className="p-0 m-0 cursor-pointer">
            {borderStyle === 'border' ? (
                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
            ) : (
                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
            )}
        </components.DropdownIndicator>
    );
}

export function ClearIndicator({ children, ...props }: ClearIndicatorProps): JSX.Element {
    return (
        <components.ClearIndicator {...props} className="p-0 m-0 cursor-pointer">
            <XMarkIcon className="h-4 w-4 text-gray-400" />
        </components.ClearIndicator>
    );
}

export function IndicatorsContainer({ children, ...props }: IndicatorsContainerProps): JSX.Element {
    const { size } = useContext(SelectContext);
    const sizeStyle = size === 'standard' ? '' : 'min-h-[2rem]';
    return (
        <components.IndicatorsContainer {...props} className={`p-0 m-0 px-2 ${sizeStyle}`}>
            {children}
        </components.IndicatorsContainer>
    );
}

export function Input(props: InputProps): JSX.Element {
    return <components.Input {...props} inputClassName="outline-none focus:ring-0 dark:text-white/[0.87]" />;
}

export function Menu({ children, ...props }: MenuProps): JSX.Element {
    const { position } = useContext(SelectContext);
    const posStyle = position === 'absolute' ? 'absolute' : 'relative';
    return (
        <components.Menu
            {...props}
            className={`${posStyle} bg-white dark:bg-white/[0.12] z-50 shadow-lg rounded-md py-1 text-base ring-1 
                       ring-black ring-opacity-5 focus:outline-none sm:text-sm`}
        >
            {children}
        </components.Menu>
    );
}

export function MenuList({ children, ...props }: MenuListProps): JSX.Element {
    return (
        <components.MenuList {...props} className="max-h-56 overflow-y-auto ">
            {children}
        </components.MenuList>
    );
}

export function Option({ children, ...props }: OptionProps): JSX.Element {
    const { size } = useContext(SelectContext);

    const focusStyle = props.isFocused
        ? 'text-white dark:text-white/[0.87] bg-indigo-600 dark:bg-indigo-500'
        : 'text-gray-900 dark:text-white/[0.87] bg-transparent';
    const data: SelectOption = props.data as SelectOption;
    return (
        <TooltipV2 message={data.disabledHint} disabled={!data.disabledHint || !data.isDisabled}>
            <components.Option
                {...props}
                className={`${focusStyle} flex items-center gap-3 cursor-default select-none relative py-2 pl-3 pr-9 ${
                    data.isDisabled ? 'opacity-[0.5]' : ''
                }`}
            >
                {data.decoration}
                <label
                    className={`${props.isSelected ? 'font-medium' : 'font-normal'} ${size === 'small' ? 'text-xs' : 'text-sm'} shrink-0`}
                >
                    {children}
                </label>
                {data.description && (
                    <span className={`${props.isFocused ? 'text-indigo-200' : 'text-gray-500 dark:text-white/[0.60]'} ml-0.5 truncate`}>
                        {data.description}
                    </span>
                )}
                {props.isSelected && (
                    <span
                        className={`${
                            props.isFocused ? 'text-white' : 'text-indigo-600'
                        } absolute inset-y-0 right-0 flex items-center pr-4`}
                    >
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                    </span>
                )}
            </components.Option>
        </TooltipV2>
    );
}

export function SingleValue({ children, ...props }: SingleValueProps): JSX.Element {
    const data = props.data as SelectOption;

    const { size } = useContext(SelectContext);

    return (
        <components.SingleValue {...props} className={`flex items-center gap-3 ${size === 'small' ? 'text-xs' : 'text-sm'}`}>
            {data.decoration}
            {children}
        </components.SingleValue>
    );
}

export function MultiValue({ children, ...props }: MultiValueProps): JSX.Element {
    const data = props.data as SelectOption;
    const selected = props.selectProps.value as SelectOption[];

    return (
        <components.MultiValueContainer {...props}>
            <span className="mr-1">
                <Badge
                    text={data.label}
                    color="gray"
                    size="small"
                    type="rounded"
                    onRemove={(): void => {
                        const rest = selected.filter((option: SelectOption) => option.value !== data.value);
                        props.setValue(rest, 'deselect-option', data);
                    }}
                />
            </span>
        </components.MultiValueContainer>
    );
}

export function Placeholder({ children, ...props }: PlaceholderProps): JSX.Element {
    const { size } = useContext(SelectContext);
    const sizeStyle = size === 'standard' ? '' : 'text-xs';
    return (
        <components.Placeholder {...props} className={`${sizeStyle} dark:text-white/[0.38]`}>
            {children}
        </components.Placeholder>
    );
}

export const IndicatorSeparator: null = null;
export const LoadingIndicator: null = null;

export default {
    Control,
    Input,
    Menu,
    MenuList,
    Option,
    SingleValue,
    // MultiValue,
    Placeholder,
    IndicatorsContainer,
    DropdownIndicator,
    ClearIndicator,
    IndicatorSeparator,
    LoadingIndicator,
};
